import { EZoraTokenClaimStatus } from '@/__generated__/useAccountAllocationsDataQuery.graphql'
import { useClaimWithSignature } from '@/hooks/useClaimWithSignature'
import { DEFAULT_CHAIN } from '@/lib/chains'
import { useClaimPollingStatus } from '@/pages/claim'
import { updateWalletClaimStatus } from '@/utils/relayUtils'
import { useLogin, usePrivy, useWallets as usePrivyWallets } from '@privy-io/react-auth'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useMemo, useState } from 'react'
import { useRelayEnvironment } from 'react-relay'
import { ViewProps } from 'reshaped'
import { Address, isAddressEqual } from 'viem'
import { ClaimButton, ClaimedButton, ConnectButton } from './ClaimButton'

export function EmbeddedWalletClaim({
  address,
  smartWalletAddress,
  externalWalletAddress,
  status,
  tokens = 0,
  textVariant,
  size,
  ...viewProps
}: {
  address: Address
  smartWalletAddress?: Address
  externalWalletAddress?: Address
  status: EZoraTokenClaimStatus
  tokens?: number
  textVariant?: 'featured-3' | 'body-2'
  size?: 'default' | 'large'
} & Omit<ViewProps, 'onClick'>) {
  const { authenticated, logout } = usePrivy()
  const { login } = useLogin()
  const { wallets: connectedWallets } = usePrivyWallets()
  const [isSwitchingChain, setIsSwitchingChain] = useState(false)
  const environment = useRelayEnvironment()
  const postHog = usePostHog()

  // Get polling status from context
  const { isPolling, isAnyClaiming, setAnyClaiming } = useClaimPollingStatus()

  const connectedEmbeddedWallet = useMemo(() => {
    return connectedWallets.find(
      (wallet) =>
        wallet.walletClientType === 'privy' &&
        isAddressEqual(wallet.address as Address, address)
    )
  }, [address, connectedWallets])

  const { mutate: claim, isPending: isClaimPending } = useClaimWithSignature({
    embeddedWallet: connectedEmbeddedWallet,
    externalWalletAddress: externalWalletAddress,
    smartWalletAddress: smartWalletAddress,
    tokenAmount: tokens,
    onSuccess: () => {
      updateWalletClaimStatus(environment, smartWalletAddress || address, 'PRIVY')

      postHog.capture('claim_success', {
        walletType: 'embedded',
        walletAddress: address,
        externalWalletAddress: externalWalletAddress || null,
        tokenAmount: tokens,
      })

      // Allow other claim buttons to be used
      setAnyClaiming(false)
    },
  })

  // Update the global claiming state when this button's claiming state changes
  useEffect(() => {
    if (isClaimPending) {
      setAnyClaiming(true)
    } else if (!isClaimPending && !isPolling) {
      setAnyClaiming(false)
    }
  }, [isClaimPending, isPolling, setAnyClaiming])

  const handleClaim = async () => {
    if (!connectedEmbeddedWallet) return

    // Set global state to disable other buttons
    setAnyClaiming(true)

    try {
      postHog.capture('claim_attempt', {
        walletType: 'embedded',
        embeddedWalletAddress: address,
        smartWalletAddress,
        tokenAmount: tokens,
      })

      // Start transaction process
      setIsSwitchingChain(true)

      if (connectedEmbeddedWallet.switchChain) {
        await connectedEmbeddedWallet.switchChain(DEFAULT_CHAIN.id)
      }

      setIsSwitchingChain(false)

      // Execute the claim
      claim()
    } catch (error) {
      console.error('Error switching chain:', error)
      setIsSwitchingChain(false)

      // Reset global claiming state on error
      setAnyClaiming(false)

      postHog.capture('claim_error', {
        walletType: 'embedded',
        embeddedWalletAddress: address,
        smartWalletAddress,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // Show claimed state only when backend confirms it
  if (status === 'CLAIMED') {
    return <ClaimedButton textVariant={textVariant} size={size} {...viewProps} />
  }

  if (!connectedEmbeddedWallet) {
    return (
      <ConnectButton
        onClick={async () => {
          postHog.capture('connect_wallet_click', {
            walletType: 'embedded',
            component: 'EmbeddedWalletClaim',
          })

          if (authenticated) await logout()
          login()
        }}
        textVariant={textVariant}
        size={size}
        {...viewProps}
      />
    )
  }

  // Determine button states
  const isLoading =
    isClaimPending || isSwitchingChain || (isPolling && status === 'NOT_CLAIMED')
  const isButtonDisabled = isLoading || tokens <= 0 || (isAnyClaiming && !isClaimPending)

  return (
    <ClaimButton
      onClick={handleClaim}
      disabled={isButtonDisabled}
      loading={isLoading}
      textVariant={textVariant}
      size={size}
      {...viewProps}
    />
  )
}
